import { type FC, useMemo } from 'react';
import { Flex, type FlexProps } from '@chakra-ui/react';

import { PlanId } from '__generated__/GQL';
import Skeleton from 'components/Skeleton';

import type * as GQL from '../../graphql/overview.gql';
import { Quota as QuotaComponent } from './components/Quota';
import { Quota, type QuotaContext } from './context';
import { CheckDepositQuota } from './quotas/CheckDepositQuota';
import { MastercardBusinessWorldDebitQuota } from './quotas/MastercardBusinessWorldDebitPaymentCardQuota';
import { SepaTransferQuota } from './quotas/SepaTransferQuota';
import { TeamMemberSeatQuota } from './quotas/TeamMemberSeatQuota';
import { VirtualPaymentCardQuota } from './quotas/VirtualPaymentCardQuota';
import { WithdrawalQuota } from './quotas/WithdrawalQuota';

export type OverviewQuotasProps = FlexProps & {
  loading?: boolean;
  data?: GQL.SubscriptionManagementOverviewQuery;
};

export const OverviewQuotas: FC<OverviewQuotasProps> = (props) => {
  const { data, loading, ...rest } = props;

  const quotas = useMemo<Record<PlanId, React.ReactNode[]>>(
    () => ({
      /**
       * 2025 plans
       */
      [PlanId.FreeMonthly_2025]: [<SepaTransferQuota key="sepa-transfer" />],
      [PlanId.StartMonthly_2025]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
      ],
      [PlanId.StartYearly_2025]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
      ],
      [PlanId.PlusMonthly_2025]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
        <MastercardBusinessWorldDebitQuota key="mastercard-business" />,
        <TeamMemberSeatQuota key="team-member-seat" />,
      ],
      [PlanId.PlusYearly_2025]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
        <MastercardBusinessWorldDebitQuota key="mastercard-business" />,
        <TeamMemberSeatQuota key="team-member-seat" />,
      ],
      [PlanId.BusinessMonthly_2025]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
        <MastercardBusinessWorldDebitQuota key="mastercard-business" />,
      ],
      [PlanId.BusinessYearly_2025]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
        <MastercardBusinessWorldDebitQuota key="mastercard-business" />,
      ],

      /**
       * 2023 plans
       */
      [PlanId.Basic]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
        <VirtualPaymentCardQuota key="virtual-payment-card" />,
      ],
      [PlanId.Plus]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
        <MastercardBusinessWorldDebitQuota key="mastercard-business" />,
        <VirtualPaymentCardQuota key="virtual-payment-card" />,
      ],
      [PlanId.Pro]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
        <MastercardBusinessWorldDebitQuota key="mastercard-business" />,
        <TeamMemberSeatQuota key="team-member-seat" />,
      ],
      [PlanId.Business]: [
        <SepaTransferQuota key="sepa-transfer" />,
        <WithdrawalQuota key="withdrawal" />,
        <CheckDepositQuota key="check-deposit" />,
        <MastercardBusinessWorldDebitQuota key="mastercard-business" />,
        <TeamMemberSeatQuota key="team-member-seat" />,
      ],

      /**
       * Discontinued plans - for pleasing TS
       */
      [PlanId.MoralPersonBasic]: [],
      [PlanId.MoralPersonBusiness]: [],
      [PlanId.MoralPersonPremium]: [],
      [PlanId.PhysicalPersonBasic]: [],
      [PlanId.PhysicalPersonPremium]: [],
    }),
    [],
  );

  if (loading || !data || !data.viewer.company.subscriptionPlan) {
    return (
      <Flex direction="column" gap="space-16">
        {[...Array(4)].map((_, index) => (
          <QuotaComponent key={index}>
            <Skeleton height="space-96" width="full" />
          </QuotaComponent>
        ))}
      </Flex>
    );
  }

  const quotaContext: QuotaContext = {
    fees: data.viewer.company.currentPlan.fees,
    planId: data.viewer.company.currentPlan.id,
    planName: data.viewer.company.currentPlan.brandName,
    quotas: data.viewer.company.subscriptionPlan.quotas,
  };

  return (
    <Quota.Provider value={quotaContext}>
      <Flex {...rest} direction="column" gap="space-16">
        {quotas[data.viewer.company.currentPlan.id]}
      </Flex>
    </Quota.Provider>
  );
};
